<template>
  <div class="flex w-full h-full space-x-2">
    <div
      :class="[
        { 'h-0 border-0 opacity-0': rollupLoading, 'w-full': rollup_metric },
        { 'w-1/2 border-r ': rollup_metric?.results?.length }
      ]">
      <div v-if="rollup_metric" class="dark:text-dark-body-text">
        <div class="flex items-center justify-between">
          <div class="flex items-center">
            <div class="mr-1 -ml-1">
              <NumbersIcon v-if="rollup_metric.input_type == 'Number'" class="w-[32px] h-[32px]" />
              <DecimalIcon v-else-if="rollup_metric.input_type == 'Decimal'" class="w-[32px] h-[32px]" />
              <PercentIcon v-else-if="rollup_metric.input_type == 'Percent'" class="w-[32px] h-[32px]" />
              <CurrencyIcon v-else-if="rollup_metric.input_type == 'Currency'" class="w-[32px] h-[32px]" />
              <StarRatingIcon v-else-if="rollup_metric.input_type == 'StarRating'" class="w-[32px] h-[32px]" />
              <BarRatingIcon v-else-if="rollup_metric.input_type == 'BarRating'" class="w-[32px] h-[32px]" />
            </div>

            <div class="flex items-center w-full">
              <div class="flex-1 w-full pt-1 font-medium truncate whitespace-nowrap overflow-ellipsis">
                {{ metric_label }}
              </div>
            </div>
          </div>
          <div
            v-if="rollup_metric"
            class="flex items-center justify-center w-8 h-8 bg-white border rounded-full cursor-pointer dark:border-dark-divider-border dark:bg-dark-screen-background"
            @click="$emit('cancel')">
            <X color="#FF0000" class="w-6 h-6 transition ease-in-out duration-side-menu-transition-duration" />
          </div>
        </div>
        <div
          class="box-border relative flex items-center content-center justify-between text-xl font-semibold sm:text-lg"
          :class="'h-fit px-3'">
          <div class="flex items-center content-center gap-1 mt-1 font-bold">
            <div :class="['text-2xl ']" class="flex font-bold text-justify truncate text-grey-light dark:text-dark-body-text">
              {{ rollup_metric.value === "blank" ? "0" : $utils.humanizeMetricValue(rollup_metric, rollup_metric.value) }}
            </div>
            <div
              class="flex items-center content-center pl-2 ml-1 h-5 rounded-full !text-[10px]"
              :class="
                rollup_metric.percentage_change < 0
                  ? '!bg-status-error-red-transparent'
                  : rollup_metric.percentage_change > 0
                    ? '!bg-status-success-green-transparent'
                    : '!bg-status-warning-yellow-transparent'
              ">
              <div
                v-if="rollup_metric.value !== 'blank' || rollup_metric.percentage_change !== 0"
                :style="{ color: `${getBackgroundColor(rollup_metric)}` }"
                v>
                <ChevronUp v-if="rollup_metric.percentage_change > 0" class="w-4 h-4" />
                <ChevronDown v-else-if="rollup_metric.percentage_change < 0" class="w-4 h-4" />
                <Tally2 v-else class="w-4 h-4 rotate-90" />
              </div>
              <div
                class="pr-2 !text-[10px]"
                :class="
                  rollup_metric.percentage_change < 0
                    ? '!text-status-error-red'
                    : rollup_metric.percentage_change > 0
                      ? '!text-status-success-green'
                      : '!text-status-warning-yellow'
                ">
                {{ !isNaN(rollup_metric.percentage_change) ? `${rollup_metric.percentage_change.toFixed(2)}%` : "0%" }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-if="rollup_metric?.history?.length" :class="['h-[50px]']" class="relative min-w-[50px] w-full min-h-[46px] ml-2">
        <MetricSummaryChart
          v-if="rollup_metric.percentage_change !== null && !hide_chart"
          class="relative w-full h-full"
          :percentage-change="rollup_metric.percentage_change"
          :is-decrease-positive="rollup_metric.metric_colors?.is_decrease_positive"
          :metric-values="rollup_metric.history"
          :chartid="rollup_metric.id" />
      </div>

      <div v-if="rollup_metric?.history?.length && !truncateBy" class="flex w-full mr-2">
        <div
          :class="{ 'pointer-events-none opacity-60': rollup_day_option_value !== 'Q' && rollup_day_option_value !== 'Y' }"
          class="flex items-center space-x-2">
          <div>{{ $t("Truncate by") }}:</div>
          <div>
            <SelectInput
              v-model="truncate_by"
              :value="truncate_by"
              value-attr="value"
              label-attr="label"
              class="w-[120px]"
              name="truncate_by"
              :input-classes="'text-left'"
              :is-box-background="true"
              :show-label="false"
              :allow-null="false"
              :items="truncate_by_options"
              :label="$t('Truncate by')" />
          </div>
        </div>
        <div class="flex ml-auto space-x-2 text-sm">
          <div
            class="cursor-pointer"
            :class="{ 'font-bold underline': rollup_day_option_value == 7 }"
            @click="() => handleSelectRollupDays(7)">
            {{ $t("1W") }}
          </div>
          <div class="cursor-pointer" :class="{ 'font-bold underline': rollup_day_option_value == 14 }" @click="handleSelectRollupDays(14)">
            {{ $t("2W") }}
          </div>
          <div
            class="cursor-pointer"
            :class="{ 'font-bold underline': rollup_day_option_value == 21 }"
            @click="() => handleSelectRollupDays(21)">
            {{ $t("3W") }}
          </div>
          <div
            class="cursor-pointer"
            :class="{ 'font-bold underline': rollup_day_option_value == 30 }"
            @click="() => handleSelectRollupDays(30)">
            {{ $t("1M") }}
          </div>
          <div
            class="cursor-pointer"
            :class="{ 'font-bold underline': rollup_day_option_value == 'Q' }"
            @click="() => handleSelectRollupDays('Q')">
            {{ $t("1Q") }}
          </div>
          <div
            class="cursor-pointer"
            :class="{ 'font-bold underline': rollup_day_option_value == 'Y' }"
            @click="() => handleSelectRollupDays('Y')">
            {{ $t("1Y") }}
          </div>
        </div>
      </div>
    </div>

    <div v-if="rollup_metric && rollup_metric?.results?.length" class="flex flex-wrap w-1/2 mt-3">
      <div class="mb-2 ml-2 font-medium">
        {{ $t("Nested Metrics") }}
      </div>
      <div
        v-for="nested_metric in rollup_metric?.results"
        :key="nested_metric.id"
        class="flex items-center w-full content-center !text-sm px-2 py-1 m-1 rounded-full">
        <div class="flex items-center w-full text-sm">
          <div>
            <div class="mr-1 -ml-1">
              <NumbersIcon v-if="nested_metric.input_type == 'Number'" class="w-[32px] h-[32px]" />
              <DecimalIcon v-else-if="nested_metric.input_type == 'Decimal'" class="w-[32px] h-[32px]" />
              <PercentIcon v-else-if="nested_metric.input_type == 'Percent'" class="w-[32px] h-[32px]" />
              <CurrencyIcon v-else-if="nested_metric.input_type == 'Currency'" class="w-[32px] h-[32px]" />
              <StarRatingIcon v-else-if="nested_metric.input_type == 'StarRating'" class="w-[32px] h-[32px]" />
              <BarRatingIcon v-else-if="nested_metric.input_type == 'BarRating'" class="w-[32px] h-[32px]" />
            </div>
          </div>
          <span class="w-20 mr-1 text-xs">{{ nested_metric.name }}</span>

          <div v-if="nested_metric.history?.length" class="w-20 h-12">
            <MetricSummaryChart
              v-if="getPercentageChange(nested_metric) !== null"
              class="relative w-full h-full"
              :percentage-change="getPercentageChange(nested_metric)"
              :is-decrease-positive="false"
              :metric-values="nested_metric.history"
              :chartid="nested_metric.id" />
          </div>
          <div class="flex-row w-20 ml-2 text-center">
            <span class="mr-1 text-lg font-medium">{{
              nested_metric.value === "blank" ? "0" : $utils.humanizeMetricValue(nested_metric, nested_metric.value)
            }}</span>
            <div class="flex justify-center w-full">
              <div
                class="flex w-fit items-center pr-2 text-center !text-[10px] rounded-full px-1"
                :style="{ color: `${getBackgroundColor(nested_metric)}`, backgroundColor: `${getBackgroundColor(nested_metric)}1F` }">
                <ChevronUp v-if="getPercentageChange(nested_metric) > 0" class="w-4 h-4" />
                <ChevronDown v-else-if="getPercentageChange(nested_metric) < 0" class="w-4 h-4" />
                <Tally2 v-else class="w-4 h-4 rotate-90" />
                <span class="font-medium">{{
                  getPercentageChange(nested_metric) ? `${getPercentageChange(nested_metric).toFixed(2)}%` : "0%"
                }}</span>
              </div>
            </div>
          </div>

          <!--          
          <span class="mr-1 font-medium !not-sr-only !text-[10px]">{{ nested_metric.value === "blank" ? "0" : $utils.humanizeMetricValue(nested_metric, nested_metric.value) }}</span>
          <div class="flex items-center pr-2 text-center !text-[10px] ml-2" :style="{ color: `${getBackgroundColor(nested_metric)}` }">
            <ChevronUp v-if="getPercentageChange(nested_metric) > 0" class="w-4 h-4" />
            <ChevronDown v-else-if="getPercentageChange(nested_metric) < 0" class="w-4 h-4" />
            <Tally2 v-else class="w-4 h-4 rotate-90" />
            <span class="font-medium">{{ getPercentageChange(nested_metric) ? `${getPercentageChange(nested_metric).toFixed(2)}%` : "0%" }}</span>
          </div> -->
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import NumbersIcon from "@/components/shared/icons/NumbersIcon.vue"
import DecimalIcon from "@/components/shared/icons/DecimalIcon.vue"
import PercentIcon from "@/components/shared/icons/PercentIcon.vue"
import CurrencyIcon from "@/components/shared/icons/CurrencyIcon.vue"
import StarRatingIcon from "@/components/shared/icons/StarRatingIcon.vue"
import BarRatingIcon from "@/components/shared/icons/BarRatingIcon.vue"
import dataService from "@/services/data.service"
import { ChevronDown, ChevronUp, Tally2, X } from "lucide-vue-next"
import MetricSummaryChart from "@/components/shared/MetricSummaryChart.vue"
import SelectInput from "@/components/shared/SelectInput.vue"

export default {
  components: {
    X,
    SelectInput,
    NumbersIcon,
    DecimalIcon,
    PercentIcon,
    CurrencyIcon,
    StarRatingIcon,
    BarRatingIcon,
    Tally2,
    ChevronUp,
    ChevronDown,
    MetricSummaryChart
  },
  props: {
    metric: null,
    record: null,
    forGraph: null,
    filters: null,
    rollupMetric: null,
    rollupLoading: null,
    truncateBy: null,
    graphLabelValue: null
  },
  emits: ["fetchedRollup", "cancel"],
  data() {
    return {
      rollup_metric: null,

      truncate_by_options: [
        { value: "day", label: this.$t("Day") },
        { value: "month", label: this.$t("Month") }
      ],
      truncate_by: "day",
      rollup_day_option_value: "Y",
      hide_chart: false
    }
  },
  computed: {
    metric_label() {
      return this.forGraph ? this.rollupMetric?.name : this.metric?.label || this.metric.name
    }
  },
  watch: {
    rollup_day_option_value() {
      if (this.rollup_day_option_value == "Q" || this.rollup_day_option_value == "Y") {
        this.getRollupMetric(true)
      }

      if (this.rollup_day_option_value !== "Q" && this.rollup_day_option_value !== "Y") {
        this.truncate_by = "day"
      }
    },
    truncate_by() {
      this.getRollupMetric(true)
    }
  },
  created() {
    this.getRollupMetric()
  },
  methods: {
    getPercentageChange(nested_metric) {
      return nested_metric.percentage_change
    },
    getBackgroundColor(nested_metric) {
      const isNegative = Math.sign(this.getPercentageChange(nested_metric))
      const GreenBorder = "#27C884"
      const RedBorder = "#F05B5B"
      const YellowBorder = "#FFBE15"

      let metric_colors = null
      if (this.forGraph) {
        metric_colors = this.$utils.getMetricByReferenceName(this.rollup_metric)?.metric_colors
      } else {
        metric_colors = this.$utils.getMetricByReferenceName(this.metric)?.metric_colors
      }
      if (isNegative === -1) {
        if (metric_colors?.is_decrease_positive) {
          return GreenBorder
        } else {
          return RedBorder
        }
      } else if (isNegative === 1) {
        if (metric_colors?.is_decrease_positive) {
          return RedBorder
        } else {
          return GreenBorder
        }
      } else if (isNegative === 0) {
        return YellowBorder
      }
      return YellowBorder
    },
    async getRollupMetric() {
      if (this.forGraph) {
        if (!this.$utils.getMetricById(this.rollupMetric.id).aggregation_method && this.metric) {
          const filters = { filter: this.filters.filter, object_types: [this.$utils.getMetricById(this.metric.id).object_type] }

          dataService.filterRecords(filters).then(async (res) => {
            const record_id = res.data.results[0]?.id

            let start_date_calc = this.$moment().startOf("day").subtract(30, "days")

            if (this.rollup_day_option_value == "Q") {
              start_date_calc = this.$moment().startOf("quarter")
            }
            if (this.rollup_day_option_value == "Y") {
              start_date_calc = this.$moment().startOf("day").subtract(365, "days")
            }

            await dataService
              .getMetricsHistoryValues({
                records: [record_id],
                target_metrics: [this.rollupMetric.id],
                page_reference: "dashboard",
                start_date: start_date_calc.format("YYYY-MM-DD"),
                end_date: this.$moment().startOf("day").format("YYYY-MM-DD"),
                truncate_by: this.truncate_by
              })
              .then((res) => {
                // We have only one record, so we can just take the first element
                // same for the metric
                this.rollup_metric = res.data[0].results[0] || null
              })
          })
        } else {
          const date_string = this.graphLabelValue
          let end_date_calc = this.$moment().startOf("day")
          let start_date_calc = this.$moment().startOf("day").subtract(30, "days")

          if (this.rollup_day_option_value == "Q") {
            start_date_calc = this.$moment().startOf("quarter")
          }
          if (this.rollup_day_option_value == "Y") {
            start_date_calc = this.$moment().startOf("day").subtract(365, "days")
          }

          if (this.truncateBy == "week") {
            // Split the input string into week and year
            const [week, year] = date_string.split("/")

            // Create a moment object with the given week and year
            const date = this.$moment().year(year).week(week)

            start_date_calc = this.$utils.clone(date.startOf("day"))
            end_date_calc = date.startOf("day").add(7, "days")
          }

          if (this.truncateBy == "month") {
            // Split the input string into month and year
            const [month, year] = date_string.split("/")

            // Create a moment object with the given month and year
            const date = this.$moment().year(year).month(month)

            start_date_calc = this.$utils.clone(date.startOf("day"))
            end_date_calc = date.startOf("day").add(1, "month")
          }

          if (this.truncateBy == "year") {
            const year = date_string

            // Create a moment object with the first day of the first week of the given year
            const date = this.$moment().year(year).week(1).startOf("week")

            start_date_calc = this.$utils.clone(date.startOf("day"))
            end_date_calc = date.startOf("day").add(1, "year")
          }

          if (this.truncateBy == "quarter") {
            // Assuming date_string is in the format "Q4/2023"
            let [quarter, year] = date_string.split("/")

            // Convert quarter to a start month
            const start_month = (parseInt(quarter.slice(1)) - 1) * 3 + 1

            // Create a moment object with the first day of the quarter
            const start_date = this.$moment()
              .year(year)
              .month(start_month - 1)
              .startOf("month")

            // Calculate the end date by adding 3 months (one quarter) to the start date
            const endDate = this.$utils.clone(start_date).add(3, "months")

            start_date_calc = this.$utils.clone(start_date.startOf("day"))
            end_date_calc = endDate.startOf("day")
          }

          await dataService
            .getRollupMetrics({
              page_reference: "dashboard",
              records_filter: this.filters.filter,
              target_metrics: [this.rollupMetric?.id],
              start_date: start_date_calc.format("YYYY-MM-DD"),
              end_date: end_date_calc.format("YYYY-MM-DD"),
              truncate_by: this.truncate_by
            })
            .then((res) => {
              this.rollup_metric = res?.data ? res.data[0] : null
              if (this.rollup_metric) {
                this.rollup_metric.original_history = this.rollup_metric?.history
              }
            })
        }
      } else {
        let start_date_calc = this.$moment().startOf("day").subtract(30, "days")

        if (this.rollup_day_option_value == "Q") {
          start_date_calc = this.$moment().startOf("quarter")
        }
        if (this.rollup_day_option_value == "Y") {
          start_date_calc = this.$moment().startOf("day").subtract(365, "days")
        }

        await dataService
          .getMetricsHistoryValues({
            page_reference: "dashboard",
            records: [this.record?.id],
            target_metrics: [this.metric?.id],
            start_date: start_date_calc.format("YYYY-MM-DD"),
            end_date: this.$moment().startOf("day").format("YYYY-MM-DD"),
            truncate_by: this.truncate_by
          })
          .then((res) => {
            this.rollup_metric = res.data[0].results[0] || null

            this.$emit("fetchedRollup")
          })
      }
    },

    handleSelectRollupDays(e) {
      this.rollup_day_option_value = e
      const plotted_values = []

      this.rollup_metric.original_history?.map((val) => {
        let days_old

        if (typeof e === "number") {
          // If 'e' is a number, calculate days_old based on the number of days
          const given = this.$moment(val.datetime, "YYYY-MM-DD")
          const current = this.$moment().startOf("day")
          days_old = this.$moment.duration(given.diff(current)).asDays() * -1
        } else if (e === "Q") {
          // If 'e' is 'Q', calculate days_old for the current quarter
          const quarterStart = this.$moment().startOf("quarter")
          const given = this.$moment(val.datetime, "YYYY-MM-DD")
          days_old = quarterStart.diff(given, "days")
        } else if (e === "Y") {
          // If 'e' is 'Y', calculate days_old for the current year
          const yearStart = this.$moment().startOf("year")
          const given = this.$moment(val.datetime, "YYYY-MM-DD")
          days_old = yearStart.diff(given, "days")
        } else {
          // Handle other cases as needed
          console.error("Unsupported value for 'e'")
          return
        }

        if ((typeof e === "number" && days_old <= e) || (e === "Q" && days_old <= 0) || (e === "Y" && days_old <= 0)) {
          plotted_values.push(val)
        }

        this.rollup_metric.history = plotted_values

        this.hide_chart = true

        setTimeout(() => {
          this.hide_chart = false
        }, 200)
      })
    }
  }
}
</script>
